/* =============================================================================
 * M.M.LaFleur — collection infinite scroll.
 *
 * Loads the next page as the foot of the grid comes into view, and turns the
 * Load More link into an in-place loader rather than a navigation.
 *
 * PROGRESSIVE ENHANCEMENT, NOT REPLACEMENT
 * ----------------------------------------
 * mm_collection.liquid still renders the paginated grid and the Load More
 * link, and both stay in place. The link is the sentinel the observer watches,
 * the manual control, and — crucially — still a real <a href> in the served
 * HTML, so the collection remains fully walkable with JavaScript off and by
 * crawlers.
 *
 * WHY THE HREF IS REMOVED AT RUNTIME
 * ----------------------------------
 * The first version relied on preventDefault() from a capture-phase listener
 * to stop the link navigating. It did not hold — clicking Load More still
 * reloaded the whole page — which means something upstream was consuming the
 * click before this handler ran. Listener order at document level is not
 * something this file can guarantee: Platter binds in <head>, Barba binds its
 * own link handling, and both run before a footer script.
 *
 * So rather than race for the event, the link is disarmed. Once JS is running,
 * the href is moved to data-mm-next and the attribute removed, which leaves
 * nothing for the browser to navigate to no matter which handler wins. The
 * element stays an <a> so the existing .mm-btn styling still applies, and
 * gains the button role and keyboard handling it now needs.
 *
 * Because the swap only happens when this script executes, the no-JS path is
 * untouched — the server still sends a working link.
 *
 * ACROSS-PAGE DEDUPING
 * --------------------
 * mm_collection.liquid collapses colourways of one style into a single card,
 * but per render: its `seen` list resets on every page. A style whose colours
 * straddle a page boundary is therefore emitted again on the next page, so
 * cards are matched on product URL and repeats dropped.
 * ========================================================================== */

(function () {
  "use strict";

  /* Platter re-injects the footer's scripts around a Barba container swap, so
     without this the observer and listeners would be bound again on every soft
     navigation. init() is re-run from the hooks below instead. */
  if (window.__mmInfiniteScrollBound) return;
  window.__mmInfiniteScrollBound = true;

  /* How early to start fetching. Roughly one viewport of runway, so the next
     batch is usually in place before the shopper reaches the end. */
  var ROOT_MARGIN = "800px 0px";

  var loading = false;
  var observer = null;
  var watched = null;
  var pending = null;

  function grid() {
    return document.querySelector(".mm-plp-main .mm-product-grid") ||
           document.querySelector(".mm-product-grid");
  }

  function foot() {
    return document.querySelector(".mm-plp-foot");
  }

  /* The trigger, in whichever state it is currently in: already disarmed, or
     still the server-rendered anchor. */
  function trigger() {
    var f = foot();
    if (!f) return null;
    return f.querySelector("[data-mm-next]") || f.querySelector("a[href]");
  }

  function nextUrl() {
    var t = trigger();
    if (!t) return "";
    return t.getAttribute("data-mm-next") || t.getAttribute("href") || "";
  }

  /* Move the href out of the browser's reach. Done on every init and after
     every append, since the foot is replaced wholesale with server markup that
     arrives armed again. */
  function disarm() {
    var f = foot();
    if (!f) return;

    var link = f.querySelector("a[href]");
    if (!link) return;

    var url = link.getAttribute("href");
    if (!url) return;

    link.setAttribute("data-mm-next", url);
    link.removeAttribute("href");
    link.setAttribute("role", "button");
    link.setAttribute("tabindex", "0");
    /* An <a> with no href loses the pointer cursor and the focus ring it had
       as a link, so both are restored explicitly. */
    link.style.cursor = "pointer";
  }

  /* The product URL a card points at, used as its identity for deduping. */
  function cardKey(node) {
    var link = node.matches && node.matches("a[href]") ? node : node.querySelector("a[href]");
    if (!link) return "";
    return (link.getAttribute("href") || "").split("?")[0];
  }

  function existingKeys(container) {
    var keys = {};
    Array.prototype.forEach.call(container.children, function (child) {
      var k = cardKey(child);
      if (k) keys[k] = true;
    });
    return keys;
  }

  function setBusy(busy) {
    var t = trigger();
    if (!t) return;
    var label = t.querySelector(".mm-eyebrow");
    if (!label) return;
    if (busy) {
      if (!t.getAttribute("data-mm-label")) {
        t.setAttribute("data-mm-label", label.textContent);
      }
      label.textContent = "Loading…";
      t.setAttribute("aria-busy", "true");
    } else {
      var original = t.getAttribute("data-mm-label");
      if (original) label.textContent = original;
      t.removeAttribute("aria-busy");
    }
  }

  function load() {
    if (loading) return;

    var url = nextUrl();
    var container = grid();
    if (!url || !container) return;

    loading = true;
    setBusy(true);

    fetch(url, { headers: { "X-Requested-With": "fetch" } })
      .then(function (r) {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var incoming = doc.querySelector(".mm-plp-main .mm-product-grid") ||
                       doc.querySelector(".mm-product-grid");
        var incomingFoot = doc.querySelector(".mm-plp-foot");

        if (!incoming) throw new Error("no grid");

        var seen = existingKeys(container);
        var added = 0;

        while (incoming.firstElementChild) {
          var card = incoming.firstElementChild;
          incoming.removeChild(card);
          var key = cardKey(card);
          if (key && seen[key]) continue;
          if (key) seen[key] = true;
          container.appendChild(card);
          added++;
        }

        var currentFoot = foot();
        if (currentFoot) {
          if (incomingFoot) {
            /* Carries the updated "You've seen X of Y" count and the next
               page's link, or no link at all once the collection is
               exhausted. It arrives armed, hence the disarm below. */
            currentFoot.innerHTML = incomingFoot.innerHTML;
          } else {
            currentFoot.remove();
          }
        }

        loading = false;
        disarm();

        /* Reveal animations, lazy media and the card carousels all bind in
           mmInit, which is written to be idempotent and re-runnable. */
        if (typeof window.mmInit === "function") window.mmInit();

        watch();

        /* A page that contributed nothing after deduping would otherwise stall
           the scroll — the sentinel is already on screen and will not fire a
           fresh intersection. Keep going until something lands. */
        if (added === 0 && nextUrl()) load();
      })
      .catch(function () {
        loading = false;
        setBusy(false);
      });
  }

  function watch() {
    var f = foot();

    if (!f || !nextUrl()) {
      if (observer && watched) observer.unobserve(watched);
      watched = null;
      return;
    }

    if (!observer) {
      if (!("IntersectionObserver" in window)) return;
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) load();
        });
      }, { rootMargin: ROOT_MARGIN });
    }

    if (watched === f) return;
    if (watched) observer.unobserve(watched);
    watched = f;
    observer.observe(f);
  }

  function init() {
    pending = null;
    loading = false;
    disarm();
    watch();
  }

  function schedule() {
    if (pending) return;
    pending = window.requestAnimationFrame(init);
  }

  function isTrigger(target) {
    if (!target || !target.closest) return null;
    var f = target.closest(".mm-plp-foot");
    if (!f) return null;
    return target.closest("[data-mm-next]") || target.closest("a");
  }

  /* Bound on window rather than document, and on the capture phase, so this is
     the earliest point in the event path. Belt and braces alongside the
     disarming above — with no href there is nothing to navigate to, but
     stopping the event here also keeps Barba from treating it as a link. */
  window.addEventListener(
    "click",
    function (event) {
      if (!isTrigger(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      load();
    },
    true
  );

  /* An <a> without an href is no longer activated by Enter, and a role=button
     element is expected to respond to Space as well. */
  window.addEventListener(
    "keydown",
    function (event) {
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      if (!isTrigger(event.target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      load();
    },
    true
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", schedule);
  window.addEventListener("popstate", schedule);

  /* Barba replaces the main container without firing an event this script can
     see, so watch the DOM — the same approach mm-header-state.js uses. */
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
}());

/* =============================================================================
 * M.M.LaFleur — preserve filter state across a facet submit.
 *
 * THE BUG
 * -------
 * Choose a size, then tick a colour, and the size is silently dropped — you
 * have to pick it again. The reverse works fine. That asymmetry is the tell.
 *
 * mm_collection.liquid builds the two kinds of facet in completely different
 * ways. Colour, product type and the boolean facets are real form controls
 * inside the filter form:
 *
 *   <input type="checkbox" name="{{ value.param_name }}" data-mm-facet-input>
 *
 * Size is not. Because one chip can stand for several underlying filter values
 * (the size_alias_map folds "Extra Small" and XS onto one chip), it is rendered
 * as a link carrying a Shopify-built URL:
 *
 *   <a href="{{ final_href }}" class="mm-size-chip" aria-pressed="...">
 *
 * So the size selection lives ONLY in the query string. It has no control in
 * the form at all.
 *
 * mm-editorial.js then submits the form natively on any facet or sort change:
 *
 *   if (el.matches("[data-mm-sort]") || el.matches("[data-mm-facet-input]")) {
 *     var form = el.closest("form");
 *     if (form) form.submit();
 *   }
 *
 * A native GET submit serialises only the form's own named controls, and the
 * form's action is the bare {{ collection.url }} with no query string. So the
 * browser rebuilds the URL from the checkboxes and the sort select alone and
 * everything else in the query string is discarded. Size goes. Clicking a size
 * chip does not have the same problem because url_to_add / url_to_remove are
 * generated server-side and already carry the rest of the active filters.
 *
 * The same loss hits any facet listed in the section's "Hide these filters"
 * setting (currently "Size Clothing Filtering"): it is skipped when the sidebar
 * renders, so it has no control either, and it is dropped on every submit.
 *
 * THE FIX
 * -------
 * Immediately before the form is submitted, copy any `filter.*` parameter that
 * is in the current URL but has no matching control in the form into a hidden
 * input. The browser then serialises it along with everything else and the
 * state survives.
 *
 * Only unrepresented parameters are carried. A checkbox that exists but is
 * unchecked is "represented" — it is deliberately absent from the submission,
 * which is exactly how you turn a colour off — so it must not be re-added here.
 * `page` and `q` are not carried either: dropping the page number on a filter
 * change is correct, and returning to page 1 is the expected behaviour.
 *
 * WHY CAPTURE PHASE
 * -----------------
 * mm-editorial.js listens for `change` on document in the bubble phase and then
 * calls form.submit(). A programmatic form.submit() does NOT fire a submit
 * event, so a submit listener alone would never see it. Listening for `change`
 * on document in the CAPTURE phase puts this ahead of mm-editorial's handler in
 * the event path, so the inputs are in place by the time it submits.
 *
 * The submit listener is still needed for the price range's real
 * <button type="submit">Apply</button>, which does fire a submit event.
 *
 * Both are delegated at document level, so this survives Barba swapping <main>
 * without any re-initialisation — which is why there is no init() hook here.
 *
 * THE DURABLE FIX is to give the size chips form controls of their own, the way
 * every other facet has: render each chip as a <button type="submit"> or as a
 * label wrapping N hidden checkboxes (one per aliased value it folds together),
 * so the form is the single source of truth for the whole filter state. That is
 * a rewrite of the size branch in mm_collection.liquid and wants its own ticket.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmFilterCarryBound) return;
  window.__mmFilterCarryBound = true;

  var FORM = "#mm-filter-form";
  var MARKER = "data-mm-filter-carry";

  function carry(form) {
    if (!form || !window.URLSearchParams) return;

    /* Clear the previous pass first. Without this a second change on the same
       page would stack a duplicate set of hidden inputs, and the same filter
       value would be submitted twice. */
    Array.prototype.slice.call(form.querySelectorAll("[" + MARKER + "]"))
      .forEach(function (node) {
        if (node.parentNode) node.parentNode.removeChild(node);
      });

    /* Every name the form can already speak for, checked or not. */
    var represented = {};
    Array.prototype.slice.call(form.elements).forEach(function (el) {
      if (el.name) represented[el.name] = true;
    });

    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }

    params.forEach(function (value, key) {
      /* Shopify namespaces every storefront filter parameter under `filter.`,
         which keeps this from sweeping up q, page, sort_by or any marketing
         parameter that happens to be on the URL. */
      if (key.indexOf("filter.") !== 0) return;
      if (represented[key]) return;

      var input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      input.setAttribute(MARKER, "1");
      form.appendChild(input);
    });
  }

  document.addEventListener(
    "change",
    function (event) {
      var el = event.target;
      if (!el || !el.matches) return;
      if (!el.matches("[data-mm-facet-input]") && !el.matches("[data-mm-sort]")) return;
      var form = el.closest && el.closest("form");
      if (form && form.matches && form.matches(FORM)) carry(form);
    },
    true
  );

  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (form && form.matches && form.matches(FORM)) carry(form);
    },
    true
  );
}());

/* =============================================================================
 * M.M.LaFleur — fold aliased size values into one active-filter chip.
 *
 * THE SYMPTOM
 * -----------
 * Selecting S and M puts FOUR chips in the active row: "S", "Small", "M",
 * "Medium".
 *
 * WHY
 * ---
 * They are not duplicates. mm_collection.liquid's size_alias_map folds several
 * catalogue values onto one canonical chip ("Small | S"), and the chip's
 * url_to_add is built to apply ALL of them at once — c_href takes the first and
 * c_extra appends the rest. So one click really does activate two filter
 * values. The active-chip row then loops f.active_values and prints
 * value.label verbatim, with no knowledge of the folding, so every underlying
 * value gets its own chip.
 *
 * THE REMOVE LINK IS ALSO WRONG
 * -----------------------------
 * In the size branch, when a value is active:
 *
 *   if c_href == '' or c_href_is_add
 *     assign c_href = value.url_to_remove
 *
 * c_extra is only appended for the INACTIVE case, so an active chip's href is
 * url_to_remove for the first active value alone. Dismissing S removes S and
 * leaves Small applied. Hiding the "Small" chip without fixing this would be
 * worse than the original: you would click "S ×" and watch it come back.
 *
 * So the keeper chip's href is rebuilt here as the current URL minus EVERY
 * value in its group, which makes one click clear the whole size.
 *
 * HOW THE GROUPING IS DERIVED
 * ---------------------------
 * Each chip's href is url_to_remove — the current URL minus exactly the one
 * parameter/value that chip stands for. Diffing the two query strings recovers
 * that pair without needing the section to publish anything.
 *
 * A chip is only considered for folding when its parameter has no control in
 * #mm-filter-form. That is precisely the set of link-rendered facets (size) and
 * suppressed facets, and it keeps colour and product type — which own real
 * checkboxes — out of this entirely.
 *
 * ON THE ALIAS TABLE BELOW
 * ------------------------
 * This duplicates the DEFAULT of the section's "Size label aliases" setting,
 * because a section setting is not visible to a footer script. That is a real
 * smell: edit the setting in the theme editor and this table does not follow.
 *
 * It is made safe rather than merely hoped about. A folded label is only
 * accepted if the canonical it maps to is actually rendered as a chip in
 * .mm-size-grid — the section's own output. If the setting is changed so the
 * two disagree, the canonical will not be found, the chip is left exactly as
 * the server sent it, and the display degrades to today's behaviour instead of
 * inventing a wrong label.
 *
 * THE DURABLE FIX is in mm_collection.liquid: fold the active-chip row through
 * the same size_alias_map the sidebar already uses, emit one chip per canonical
 * label, and give it a url_to_remove built from every value it folds. Then this
 * whole block and its duplicated table go away. Not done here because that file
 * is 36KB — past the base64 truncation threshold for themeFilesUpsert and too
 * large to retype byte-exact with confidence. Own ticket.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmChipFoldBound) return;
  window.__mmChipFoldBound = true;

  var CARRY = "data-mm-filter-carry";
  var DONE = "data-mm-chips-folded";

  /* Mirrors the default of section.settings.size_alias_map. Keys lowercased. */
  var ALIASES = {
    "extra small": "XS",
    "small": "S",
    "medium": "M",
    "large": "L",
    "extra large": "XL",
    "double extra large": "XXL",
    "2x large": "XXL"
  };

  var pending = null;

  /* The chip's own text, ignoring the inline <svg> close icon beside it. */
  function labelOf(el) {
    var out = "";
    Array.prototype.forEach.call(el.childNodes, function (n) {
      if (n.nodeType === 3) out += n.nodeValue;
    });
    return out.trim();
  }

  function relabel(el, text) {
    var written = false;
    Array.prototype.forEach.call(el.childNodes, function (n) {
      if (n.nodeType !== 3 || !n.nodeValue.trim()) return;
      n.nodeValue = written ? "" : text;
      written = true;
    });
  }

  /* Recover the one parameter/value a url_to_remove link strips, by diffing it
     against the URL currently in the address bar. */
  function removedPair(href, current) {
    var target;
    try {
      target = new URL(href, window.location.href).searchParams;
    } catch (e) {
      return null;
    }

    var have = {};
    target.forEach(function (v, k) {
      var s = k + "\u0000" + v;
      have[s] = (have[s] || 0) + 1;
    });

    var found = null;
    current.forEach(function (v, k) {
      if (found) return;
      var s = k + "\u0000" + v;
      if (have[s]) have[s] -= 1;
      else found = { key: k, value: v };
    });
    return found;
  }

  function urlWithout(pairs) {
    var p = new URLSearchParams(window.location.search);
    pairs.forEach(function (pair) {
      var kept = p.getAll(pair.key).filter(function (v) { return v !== pair.value; });
      p.delete(pair.key);
      kept.forEach(function (v) { p.append(pair.key, v); });
    });
    /* Removing a filter should land on page 1, not deep in the old pagination. */
    p.delete("page");
    var qs = p.toString();
    return window.location.pathname + (qs ? "?" + qs : "");
  }

  function fold() {
    pending = null;

    if (!window.URLSearchParams || !window.URL) return;

    var wrap = document.querySelector(".mm-chips");
    if (!wrap || wrap.getAttribute(DONE) === "1") return;

    /* The canonical labels the section actually rendered. Nothing is folded
       onto a label that is not in here. */
    var canonical = {};
    Array.prototype.slice.call(document.querySelectorAll(".mm-size-chip"))
      .forEach(function (chip) {
        var l = (chip.textContent || "").trim();
        if (l) canonical[l] = true;
      });
    if (!Object.keys(canonical).length) return;

    /* Parameter names the filter form owns. Carried inputs are excluded — they
       are this file's own work, not controls the section rendered. */
    var represented = {};
    var form = document.querySelector("#mm-filter-form");
    if (form) {
      Array.prototype.slice.call(form.elements).forEach(function (el) {
        if (el.name && !el.hasAttribute(CARRY)) represented[el.name] = true;
      });
    }

    var current = new URLSearchParams(window.location.search);
    var groups = {};
    var order = [];

    Array.prototype.slice.call(wrap.querySelectorAll("a.mm-chip")).forEach(function (chip) {
      var href = chip.getAttribute("href");
      if (!href) return;

      var pair = removedPair(href, current);
      if (!pair) return;
      if (represented[pair.key]) return;

      var label = labelOf(chip);
      if (!label) return;

      var target = ALIASES[label.toLowerCase()] || label;
      if (!canonical[target]) return;

      if (!groups[target]) { groups[target] = []; order.push(target); }
      groups[target].push({ chip: chip, pair: pair, label: label });
    });

    order.forEach(function (target) {
      var members = groups[target];
      if (members.length < 2) return;

      /* Prefer the chip already showing the canonical label, so the row keeps
         its original left-to-right order wherever possible. */
      var keeper = members[0];
      members.forEach(function (m) { if (m.label === target) keeper = m; });

      var pairs = members.map(function (m) { return m.pair; });

      relabel(keeper.chip, target);
      keeper.chip.setAttribute("href", urlWithout(pairs));

      members.forEach(function (m) {
        if (m === keeper) return;
        m.chip.setAttribute("hidden", "hidden");
        m.chip.style.display = "none";
      });
    });

    wrap.setAttribute(DONE, "1");
  }

  function schedule() {
    if (pending) return;
    pending = window.requestAnimationFrame(fold);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fold);
  } else {
    fold();
  }

  document.addEventListener("shopify:section:load", schedule);
  window.addEventListener("popstate", schedule);

  /* Barba swaps <main>, which brings a fresh chip row with it. */
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
}());
